import { Calendar } from '@/_shared/components/calendar/index.js';
import type { Column } from '@tanstack/react-table';
import { zone } from '@this/shared/dist/tools/datetime.js';
import { de } from 'date-fns/locale';
import { useCallback } from 'react';
import type { DateRange } from 'react-day-picker';
import { resetFilter } from '../../_shared/reset.js';
import { typedSetDateColumnFilterValue } from '../filter/index.js';
import { DATE_FILTER_OPERATION, type DateFilterValuesWithoutInArray } from '../filter/operation.js';
import type { DateFilterSchemaWithoutInArray } from '../filter/schema.js';
import { useStyles } from './styles.js';

type DateFilterProps<TData, TValue> = {
    column: Column<TData, TValue>;
    dropdownValue: DateFilterValuesWithoutInArray['id'];
};

export function DateFilter<TData, TValue>({ column, dropdownValue }: DateFilterProps<TData, TValue>) {
    const styles = useStyles();

    const filter = column.getFilterValue() as DateFilterSchemaWithoutInArray | undefined;
    const value1 = filter && 'value1' in filter ? filter.value1 : undefined;
    const value2 = filter && 'value2' in filter ? filter.value2 : undefined;

    const onRangeChange = useCallback(
        (date: DateRange | undefined) => {
            typedSetDateColumnFilterValue(column, {
                operation: dropdownValue,
                value1: date?.from,
                value2: date?.to,
            });
        },
        [column, dropdownValue]
    );

    const onSingleChange = useCallback(
        (date: Date | undefined) => {
            if (date) {
                typedSetDateColumnFilterValue(column, {
                    operation: dropdownValue,
                    value1: date,
                });
            } else {
                resetFilter(column);
            }
        },
        [column, dropdownValue]
    );

    return (
        (dropdownValue === DATE_FILTER_OPERATION.between.id ||
            dropdownValue === DATE_FILTER_OPERATION.equals.id ||
            dropdownValue === DATE_FILTER_OPERATION.before.id ||
            dropdownValue === DATE_FILTER_OPERATION.after.id) && (
            <div className={styles.calendarContainer}>
                {dropdownValue === DATE_FILTER_OPERATION.between.id ? (
                    <Calendar
                        locale={de}
                        timeZone={zone}
                        autoFocus
                        mode="range"
                        defaultMonth={value1}
                        selected={{
                            from: value1,
                            to: value2,
                        }}
                        onSelect={onRangeChange}
                        numberOfMonths={1}
                    />
                ) : dropdownValue === DATE_FILTER_OPERATION.equals.id ||
                  dropdownValue === DATE_FILTER_OPERATION.before.id ||
                  dropdownValue === DATE_FILTER_OPERATION.after.id ? (
                    <Calendar
                        locale={de}
                        timeZone={zone}
                        autoFocus
                        mode="single"
                        defaultMonth={value1}
                        selected={value1}
                        onSelect={onSingleChange}
                        numberOfMonths={1}
                    />
                ) : null}
            </div>
        )
    );
}
